import { FunctionComponent } from 'react';
import { faTrash, faPen, faPlay, faPause } from '@fortawesome/pro-regular-svg-icons';
import { type Automation } from '@spike/automation-model';
import { TableRow } from 'components/UI/V3/Table/Row/TableRow';
import { TableCell } from 'components/UI/V3/Table/Cell/TableCell';
import ThreeDotsDropdown from 'components/UI/V3/ThreeDotsDropdown';
import styles from './AutomationsRow.module.scss';

interface Props {
    automation: Automation;
    isLast: boolean;
    onEdit?: (automation: Automation) => void;
    onDelete?: (automation: Automation) => void;
    onToggleStatus?: (automation: Automation) => void;
    isDropdownOpen?: boolean;
    onDropdownToggle?: (isOpen: boolean) => void;
}

const STATUS_LABELS: Record<string, string> = {
    draft: 'Draft',
    active: 'Active',
    paused: 'Inactive',
    archived: 'Archived'
};

const TRIGGER_LABELS: Record<string, string> = {
    action: 'Event Based',
    schedule: 'Date Based',
    filter: 'Scheduled Filter'
};

const AutomationsRow: FunctionComponent<Props> = ({
    automation,
    isLast,
    onEdit,
    onDelete,
    onToggleStatus,
    isDropdownOpen = false,
    onDropdownToggle
}) => {
    const triggerLabel = TRIGGER_LABELS[automation.trigger.kind] ?? 'Event Based';
    const isActive = automation.status === 'active';

    // Archived automations are out of circulation, so the toggle would only
    // offer a state the list is not meant to bring them back into.
    const canToggle = automation.status !== 'archived';

    const formatDate = (iso: string) => {
        const d = new Date(iso);
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const options = [
        ...(canToggle
            ? [
                  {
                      id: 'toggle-status',
                      label: isActive ? 'Deactivate' : 'Activate',
                      icon: isActive ? faPause : faPlay,
                      onClick: () => onToggleStatus?.(automation)
                  }
              ]
            : []),
        {
            id: 'edit',
            label: 'Edit',
            icon: faPen,
            onClick: () => onEdit?.(automation)
        },
        {
            id: 'delete',
            label: 'Delete',
            icon: faTrash,
            onClick: () => onDelete?.(automation)
        }
    ];

    return (
        <TableRow className={styles.row}>
            <TableCell className={styles.nameCell}>
                <p className={styles.name}>{automation.name}</p>
            </TableCell>

            <TableCell>
                <span className={`${styles.statusBadge} ${styles[automation.status]}`}>
                    {STATUS_LABELS[automation.status] ?? automation.status}
                </span>
            </TableCell>

            <TableCell>
                <p className={styles.text}>{triggerLabel}</p>
            </TableCell>

            <TableCell>
                <p className={styles.text}>{formatDate(automation.createdAt)}</p>
            </TableCell>

            <TableCell>
                <p className={styles.text}>{formatDate(automation.updatedAt)}</p>
            </TableCell>

            <TableCell
                isActions
                onClick={() => onDropdownToggle?.(!isDropdownOpen)}
            >
                <ThreeDotsDropdown
                    options={options}
                    direction={isLast ? 'up' : 'down'}
                    usePortal
                />
            </TableCell>
        </TableRow>
    );
};

export default AutomationsRow;
